import type { ColumnDef, DocumentStatus } from '@project/contracts'
import { DOCUMENT_STATUS } from '@project/contracts'
import { StatusMark, type ColumnDef as TableColumn } from '../design/index.js'
import { asText, formatRate } from './format.js'

/**
 * One mapping from a document's column metadata to the table, used by every
 * document type. The design system draws text, doc numbers, dates, money,
 * quantities and status itself; `rate` and `user` have no cell type of their
 * own, so they are drawn here rather than pushed into the design package.
 */
export const toTableColumns = <T extends Record<string, unknown>>(
  columns: readonly ColumnDef[],
): TableColumn<T>[] =>
  columns.map((c) => {
    const column: TableColumn<T> = { key: c.key, header: c.label }
    if (c.width !== undefined) column.width = c.width
    if (c.align !== undefined) column.align = c.align

    if (c.type === 'rate') {
      column.align = c.align ?? 'right'
      column.render = (row) => <span className="num">{formatRate(row[c.key])}</span>
      return column
    }
    // A document's status says which approval it waits at (WF-001).
    if (c.type === 'status' && c.key === 'documentStatus') {
      column.render = (row) => {
        const status = asText(row[c.key])
        return (DOCUMENT_STATUS as readonly string[]).includes(status)
          ? (
            <StatusMark
              status={status as DocumentStatus}
              stage={asText(row['statusStage']) || null}
              // A draft discarded before it was ever numbered (PROJ3-002).
              discarded={status === 'CANCELLED' && asText(row['documentNo']) === ''}
            />
          )
          : status
      }
      return column
    }
    if (c.type === 'user') {
      column.render = (row) => asText(row[c.key])
      return column
    }
    if (c.type !== undefined) column.type = c.type
    return column
  })
